import type IntlService from 'ember-intl/services/intl';
import { warnIfNotDateOnly } from './warn-if-not-date-only';

/**
 * Format ISO date string (YYYY-MM-DD) to localized date using UTC timezone
 * Prevents timezone shifting issues where dates display as the previous day
 *
 * Date-only input ONLY. A full ISO instant has its time and offset discarded,
 * which can render the wrong day; such input is reported via `warnIfNotDateOnly`
 * rather than repaired, because the correct rendering depends on whether the
 * value is a calendar date or a real event. Format instants at the call site.
 *
 * @param dateStr - ISO date string or 'N/A'
 * @param intl - Ember Intl service for localization
 * @param yearFormat - Format for year display ('numeric' for 4-digit, '2-digit' for 2-digit)
 * @returns Formatted date string (e.g., "12/25/2025" or "12/25/25") or 'N/A'
 *
 * @example
 * formatISODateToDisplay("2025-01-15", intl, "numeric")  // Returns "01/15/2025"
 * formatISODateToDisplay("2025-01-15", intl, "2-digit")  // Returns "01/15/25"
 */
export function formatISODateToDisplay(
  dateStr: string,
  intl: IntlService,
  yearFormat: 'numeric' | '2-digit' = 'numeric'
): string {
  if (!dateStr || dateStr === 'N/A') return 'N/A';

  warnIfNotDateOnly(dateStr, 'formatISODateToDisplay');

  try {
    // Parse as UTC to prevent timezone shifting
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
      return intl.formatDate(date, {
        month: '2-digit',
        day: '2-digit',
        year: yearFormat,
        timeZone: 'UTC',
      });
    }

    // Fallback for non-ISO dates
    const date = new Date(dateStr);
    return intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: yearFormat,
      timeZone: 'UTC',
    });
  } catch {
    return dateStr;
  }
}
